// Export a map bundle as a downloadable zip.
//
// The zip contains:
//   - map.json                 — the editor's current in-memory json (with
//                                `asset://` URLs rewritten to `assets/<name>`).
//   - assets/*                 — the bytes of every imported mesh / HDR /
//                                thumbnail referenced by the json.
//   - behaviors/*.ts           — source of every behavior referenced.
//   - materials/*.ts           — source of every bundled material module.
//   - thumbnail.png            — when the bundle has one.
//
// TS sources are fetched via Vite's `?raw` query param. That only works while
// the dev server is running (production builds inline + minify modules). The
// editor is dev-only for now so this is fine; the prod path would instead
// embed the source at build time.

import JSZip from 'jszip';
import { getBundle, type MapBundle } from '../../maps/registry';
import type { MapJSON, ObjectNode } from '../../maps/MapJSON';
import { isMaterialPreset, isMaterialModule } from '../../maps/MapJSON';
import { isAssetUrl, parseAssetUrl, getAsset, type EditorAsset } from './EditorAssets';

const INLINE_MAP_KEYS = ['mapColor', 'mapNormal', 'mapRoughness', 'mapMetalness', 'mapAo', 'mapEmissive'] as const;
type InlineMapKey = typeof INLINE_MAP_KEYS[number];

export async function exportBundleToBlob(bundleId: string, json: MapJSON): Promise<Blob> {
  const base = getBundle(bundleId);
  if (!base) throw new Error(`[exportZip] no bundle on disk for id "${bundleId}"`);

  const zip = new JSZip();

  // Walk the JSON and rewrite any `asset://` URL to `assets/<name>`. Track
  // each referenced asset so we pack its bytes too.
  const referenced = new Map<string, EditorAsset>();
  const rewrite = (u: string | undefined): string | undefined => {
    if (!u || !isAssetUrl(u)) return u;
    const id = parseAssetUrl(u);
    const a = id ? getAsset(id) : undefined;
    if (!a) return u;
    referenced.set(a.id, a);
    return `assets/${a.name}`;
  };

  const cloned: MapJSON = structuredClone(json);
  if (cloned.ibl?.hdrPath) cloned.ibl = { ...cloned.ibl, hdrPath: rewrite(cloned.ibl.hdrPath) };
  if (cloned.pickerCard?.thumbnail) {
    cloned.pickerCard = { ...cloned.pickerCard, thumbnail: rewrite(cloned.pickerCard.thumbnail) ?? '' };
  }
  const walk = (nodes: ObjectNode[] | undefined): void => {
    if (!nodes) return;
    for (const n of nodes) {
      if (n.type === 'modelRef') (n as { src: string }).src = rewrite(n.src) ?? n.src;
      // Inline-PBR texture slots — rewrite each map URL alongside packing
      // its bytes into the zip.
      const m = n.material as Record<string, unknown> | undefined;
      if (m && !isMaterialPreset(n.material) && !isMaterialModule(n.material)) {
        for (const key of INLINE_MAP_KEYS) {
          const cur = m[key];
          if (typeof cur === 'string') {
            const next = rewrite(cur);
            if (next) m[key] = next;
          }
        }
      }
      if (n.type === 'group') walk(n.children);
    }
  };
  walk(cloned.objects);

  zip.file('map.json', JSON.stringify(cloned, null, 2));

  for (const a of referenced.values()) {
    zip.file(`assets/${a.name}`, a.bytes);
  }

  await includeSources(zip, base, 'behaviors');
  await includeSources(zip, base, 'materials');

  const thumb = await fetchOrNull(`/src/maps/bundles/${bundleDirName(base)}/thumbnail.png`);
  if (thumb) zip.file('thumbnail.png', thumb);

  return zip.generateAsync({ type: 'blob' });
}

export function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

async function includeSources(
  zip: JSZip,
  bundle: MapBundle,
  dir: 'behaviors' | 'materials'
): Promise<void> {
  const loaders = dir === 'behaviors' ? bundle.behaviorLoaders : bundle.materialLoaders;
  const names = Object.keys(loaders);
  await Promise.all(names.map(async (name) => {
    const src = await fetchSource(bundle, dir, name);
    if (src !== null) zip.file(`${dir}/${name}.ts`, src);
  }));
}

async function fetchSource(
  bundle: MapBundle,
  dir: 'behaviors' | 'materials',
  name: string
): Promise<string | null> {
  const candidates = [bundleDirName(bundle), bundle.id];
  for (const dirName of candidates) {
    const url = `/src/maps/bundles/${dirName}/${dir}/${name}.ts?raw`;
    const res = await fetch(url);
    if (res.ok) return res.text();
  }
  const sharedUrl = `/src/maps/shared/${dir}/${name}.ts?raw`;
  const res = await fetch(sharedUrl);
  if (res.ok) return res.text();
  return null;
}

async function fetchOrNull(url: string): Promise<Blob | null> {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    return await res.blob();
  } catch {
    return null;
  }
}

function bundleDirName(bundle: MapBundle): string {
  const map: Record<string, string> = {
    boxingRing: 'boxing-ring-sun',
    boxingOnTheMoon: 'boxing-ring-moon',
    spaceshipInRings: 'spaceship-in-rings',
    sandbox: 'sandbox'
  };
  return map[bundle.id] ?? bundle.id;
}
